// Counterpart of town-square's components/arty/ArtyHistory.tsx. The flyout resumes only
// standalone chats: a chat about an image or collection belongs to that page's own chat,
// the same split client's ConversationControls draws on /chat.
import { useTranslation } from 'react-i18next';
import { ChevronsDown, Loader, MessageCircle } from 'lucide-react';

import { useMyChats } from '~/hooks/useMyChats';

import { useArtyFlyoutView } from './ArtyFlyoutProvider';

// Spark chats are titled from the same opening line, so the time is what tells them apart. The
// list is ordered by when a chat began (the `myChats` resolver), so that is the time shown.
function when(value: string) {
  return new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function HistoryRow({ chat, current, onSelect }: { chat: any; current: boolean; onSelect: (id: string) => void }) {
  const { t } = useTranslation('nav');

  return (
    <li>
      <button
        type="button"
        aria-current={current ? 'true' : undefined}
        className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-gray-100 ${current ? 'bg-gray-100' : ''}`}
        onClick={() => onSelect(chat.id)}
      >
        <MessageCircle className="size-4 shrink-0 text-gray-500" />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">{chat.title || t('chat.untitled')}</span>
          <span className="block text-xs text-gray-500">{when(chat.createdAt)}</span>
        </span>
      </button>
    </li>
  );
}

export function FlyoutHistory() {
  const { t } = useTranslation('chat');
  const { chat, selectChat } = useArtyFlyoutView();
  const { t: tc } = useTranslation('common');
  const { chats, loading, hasNextPage, loadMore, loadingMore } = useMyChats({ type: 'standalone' });

  if (loading && chats.length === 0) {
    return <p className="px-4 py-5 text-sm text-gray-500">{t('controls.loading-conversations')}</p>;
  }

  if (chats.length === 0) {
    return <p className="px-4 py-5 text-sm text-gray-500">{t('controls.no-conversations-yet')}</p>;
  }

  return (
    <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain p-2">
      {chats.map((row) => {
        return <HistoryRow key={row.id} chat={row} current={row.id === chat.conversationId} onSelect={selectChat} />;
      })}
      {hasNextPage && (
        <li>
          <button
            type="button"
            disabled={loadingMore}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-gray-100"
            onClick={loadMore}
          >
            {loadingMore ? (
              <Loader className="size-4 shrink-0 animate-spin text-gray-500" />
            ) : (
              <ChevronsDown className="size-4 shrink-0 text-gray-500" />
            )}
            <span className="min-w-0 flex-1 truncate">{tc('load-more')}</span>
          </button>
        </li>
      )}
    </ul>
  );
}
